import React, { useState, useEffect } from 'react';
import { Modal, Button, Container, Row, Col, Badge, Spinner } from 'react-bootstrap';
import api from '../../seller-apis/api';
import './NoticePopup.css';
import ImageCarousel from './ImageCarousel';

export const NoticePopup = () => {
  const [notices, setNotices] = useState([]);
  const [currentNoticeIndex, setCurrentNoticeIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [acknowledgedNotices, setAcknowledgedNotices] = useState({});
  const [acknowledging, setAcknowledging] = useState({});
  const currentVendorId = localStorage.getItem('vendorId');
  const [isExpanded, setIsExpanded] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
      fetchHomeNotices();
      setCurrentNoticeIndex(0);
  }, []);

  const fetchHomeNotices = async () => {
    try {
      const response = await api.notices.getActiveHomeNotices(currentVendorId);
      const filteredNotices = response?.data?.data?.filter(notice => notice.type === 'Home Page') || [];
      setNotices(filteredNotices);
      updateAcknowledgementStatus(filteredNotices);
      setLoading(true);
      if(filteredNotices.length > 0) {
        setIsOpen(true)
      }
      setLoading(false);
    } catch (err) {
      setError('Failed to fetch notices');
      setLoading(false);
    }
  };

  const updateAcknowledgementStatus = (noticesList) => {
    const newAcknowledgedMap = {};
    noticesList.forEach(notice => {
      const isAcknowledged = notice.acknowledgeUserIds?.some(ack => ack.vendorId === currentVendorId);
      newAcknowledgedMap[notice._id] = !!isAcknowledged;
    });
    setAcknowledgedNotices(newAcknowledgedMap);
  };
    const acknowledgeNotice = async (noticeId) => {
    setAcknowledging(prev => ({ ...prev, [noticeId]: true }));
    try {
        const payload = { _id: noticeId, vendorId: currentVendorId };
        await api.notices.acknowledgeNotice(payload);
        
        // Remove the acknowledged notice from the notices array
        const updatedNotices = notices.filter(notice => notice._id !== noticeId);
        setNotices(updatedNotices);
        
        // Update the acknowledgement status map
        setAcknowledgedNotices(prev => ({ ...prev, [noticeId]: true }));
        
        // Handle navigation after removing a notice
        if (updatedNotices.length === 0) {
        // No notices left, close the modal
        setIsOpen(false);
        } else if (currentNoticeIndex >= updatedNotices.length) {
        // If current index is now out of bounds, move to the last available notice
        setCurrentNoticeIndex(updatedNotices.length - 1);
        }
        // If there are still notices and the current index is valid, we stay at the same index
        // which now shows the next notice that moved up in the array
        
    } catch (error) {
        alert('Failed to acknowledge notice. Please try again.');
    } finally {
        setAcknowledging(prev => ({ ...prev, [noticeId]: false }));
    }
    };


  const formatPublishDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  // Function to get images from notice data
  const getNoticeImage = (notice) => {
    if (!notice) return null;
    
    // Look for an image in various locations of the notice object
    if (notice.image && notice.image.length > 0) {
      return notice.image[0];
    }
    
    if (notice.url && notice.url.length > 0) {
      // Handle string URLs
      if (typeof notice.url[0] === 'string') {
        return notice.url[0];
      }
      // Handle object URLs
      else if (typeof notice.url[0] === 'object' && notice.url[0].url) {
        return notice.url[0].url;
      }
    }
    
    // Default image if none is found
    return "https://www.toolsvilla.in/admin/assets/img/Toolsvilla_logo.png";
  };

  const isValidImageUrl = (url) => {
    if (!url || typeof url !== 'string') return false;
    
    // Simple check for common image extensions
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg', '.bmp'];
    const lowercaseUrl = url.toLowerCase();
    
    return imageExtensions.some(ext => lowercaseUrl.includes(ext)) || 
           lowercaseUrl.includes('image') ||
           lowercaseUrl.includes('media');
  };
  
  // Function to extract all possible images from a notice
  const getNoticeImages = (notice) => {
    if (!notice) return [];
    
    let images = [];
    
    // Check if there are images in the image array
    if (notice.image && notice.image.length > 0) {
      images = images.concat(notice.image.filter(url => url && url.trim() !== ''));
    }
    
    // Check if there are images in the url array (handle both formats)
    if (notice.url && notice.url.length > 0) {
      // Handle cases where url is an array of strings
      if (typeof notice.url[0] === 'string') {
        images = images.concat(notice.url.filter(url => url && url.trim() !== ''));
      } 
      // Handle cases where url is an array of objects with url property
      else if (typeof notice.url[0] === 'object') {
        const urlImages = notice.url
          .filter(item => item && typeof item === 'object' && item.url && item.url.trim() !== '')
          .map(item => item.url)
          .filter(url => isValidImageUrl(url));
        
        images = images.concat(urlImages);
      }
    }
    
    // Check if there are images in attachments
    if (notice.attachments && notice.attachments.length > 0) {
      let attachmentImages = [];
      
      if (Array.isArray(notice.attachments[0])) {
        // Handle nested array case
        notice.attachments.forEach(attachmentGroup => {
          if (Array.isArray(attachmentGroup)) {
            const imageAttachments = attachmentGroup
              .filter(attachment => attachment && isValidImageUrl(attachment));
            attachmentImages = attachmentImages.concat(imageAttachments);
          }
        });
      } else {
        // Handle flat array case
        const imageAttachments = notice.attachments
          .filter(attachment => attachment && isValidImageUrl(attachment));
        attachmentImages = attachmentImages.concat(imageAttachments);
      }
      
      images = images.concat(attachmentImages);
    }
    
    // Return unique images
    return [...new Set(images)].filter(img => img && isValidImageUrl(img));
  };

  const navigateToNextNotice = () => {
    if (currentNoticeIndex < notices.length - 1) {
      setCurrentNoticeIndex(currentNoticeIndex + 1);
      setIsExpanded(false);
    } else {
      setIsOpen(false)// Close modal if on the last notice
    }
  };

   // Function to render truncated message with View more/less toggle
   const renderTruncatedMessage = (message) => {
    if (!message) return null;
    
    // Decode HTML entities
    const decodeHTML = (html) => {
      const txt = document.createElement('textarea');
      txt.innerHTML = html;
      return txt.value;
    };
    
    // Extract plain text for length calculation
    const extractPlainText = (html) => {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = html;
      return tempDiv.textContent || tempDiv.innerText || '';
    };
    
    const decodedMessage = decodeHTML(message);
    const plainText = extractPlainText(decodedMessage);
    const maxLength = 300;
    const isLongText = plainText.length > maxLength;
    
    if (!isLongText) {
      return <div dangerouslySetInnerHTML={{ __html: decodedMessage }} />;
    }
    
    // For longer text that needs truncation
    return (
      <>
        <div 
          dangerouslySetInnerHTML={{ 
            __html: isExpanded 
              ? decodedMessage 
              : decodedMessage.substring(0, decodedMessage.indexOf(plainText.substring(maxLength)) + maxLength) + '...'
          }} 
        />
        <Button 
          variant="link" 
          className="notice-toggle-btn p-0 text-teal" 
          onClick={() => setIsExpanded(!isExpanded)}
        >
          {isExpanded ? 'View less' : 'View more'}
        </Button>
      </>
    );
  };
  const navigateToPrevNotice = () => {
    if (currentNoticeIndex > 0) {
      setCurrentNoticeIndex(currentNoticeIndex - 1);
      setIsExpanded(false);
    }
  };

  const NoticeImageDisplay = ({ currentNotice }) => {
    const images = getNoticeImages(currentNotice);
    
    // If no images found, don't render anything
    if (images.length === 0) {
      return null; // Return null instead of default image
    }
    
    // Otherwise, show carousel with the available images
    return (
      <ImageCarousel 
        images={images}
        title={currentNotice?.title}
        onError={(e) => {
          e.target.onerror = null;
          e.target.style.display = 'none'; // Hide failed images completely
        }}
      />
    );
  };
  const currentNotice = notices[currentNoticeIndex];
  const totalNotices = notices.length;
  return (
    <Modal 
      show={isOpen} 
      onHide={()=>setIsOpen(false)} 
      size="md" 
      centered 
      className="notice-popup"
      dialogClassName="notice-dialog"
    >
      <Modal.Header closeButton className="notice-modal-header">
        <Modal.Title className="notice-modal-title">Notice</Modal.Title>
      </Modal.Header>
      
      <Modal.Body className="p-0">
        {loading ? (
          <div className="text-center py-4">
            <Spinner animation="border" variant="secondary" size="sm" className="me-2" />
            <span className="text-secondary fs-6">Loading notices...</span>
          </div>
        ) : error ? (
          <div className="text-danger text-center py-3">{error}</div>
        ) : notices.length === 0 ? (
          <div className="text-secondary text-center py-4 fs-6">No notices available</div>
        ) : (
          <div>
            <div className="notice-content-wrapper">
            <div className={`notice-body ${getNoticeImages(currentNotice).length === 0 ? 'no-image' : ''}`}>
              <NoticeImageDisplay currentNotice={currentNotice} />   
                
                
                <div className="notice-content-container">
                  <div className="notice-header mb-2">
                    <h5 className="notice-title">{currentNotice?.title || 'Untitled Notice'}</h5>
                    <p className="notice-date">Published: {formatPublishDate(currentNotice?.publishDate)}</p>
                  </div>
                  <div className="notice-message">
                    {renderTruncatedMessage(currentNotice?.message)}
                  </div>
                </div>
              </div>
            </div>
            
            <div className="notice-footer">
              <div className="notice-navigation">
                <button 
                  onClick={navigateToPrevNotice} 
                  disabled={currentNoticeIndex === 0}
                  className="nav-button prev-button"
                >
                  <span aria-hidden="true">&laquo;</span>
                </button>
                
                <span className="notice-counter">Notices {currentNoticeIndex + 1}/{totalNotices}</span>
                
                <button 
                  onClick={navigateToNextNotice} 
                  className="nav-button next-button"
                >
                  {currentNoticeIndex === notices.length - 1 ? (
                    <span aria-hidden="true">&times;</span>
                  ) : (
                    <span aria-hidden="true">&raquo;</span>
                  )}
                </button>
              </div>
              
              {currentNotice?.acknowledgement === 'Yes' && (
                <div className="notice-actions">
                    <Button
                    onClick={() => acknowledgeNotice(currentNotice._id)}
                    disabled={acknowledging[currentNotice._id]}
                    className="acknowledge-btn"
                    >
                    {acknowledging[currentNotice._id] ? 'Processing...' : 'Acknowledge'}
                    </Button>
                </div>
                )}
            </div>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default NoticePopup;